import Popover from '@mui/material/Popover/Popover'
import useAct from 'Workflows/hooks/useAct'
import ServerWaitButton from 'Shared/components/ServerWaitButton'
import Button from 'Shared/components/Button'
import BackLink from 'Shared/components/BackLink'
import './TopBar.scoped.scss'

export default function Topbar() {
  const act = useAct()
  const { id, name, state, openingTime, closingTime, enforceSubscriptions, organisationName, exitWorkflowFilter } = useSel(s => s.builder.workflow)
  const backLink = `/admin/workflows/${id}/overview`

  const [hoursAnchorEl, setHoursAnchorEl] = useState(null)
  const [newOpeningTime, setNewOpeningTime] = useState('')
  const [newClosingTime, setNewClosingTime] = useState('')

  const [subscriptionsAnchorEl, setSubscriptionsAnchorEl] = useState(null)
  const [newEnforceSubscriptions, setNewEnforceSubscriptions] = useState(false)

  useEffect(() => {
    setNewOpeningTime(openingTime || '')
    setNewClosingTime(closingTime || '')
  }, [openingTime, closingTime])

  useEffect(() => {
    setNewEnforceSubscriptions(enforceSubscriptions || false)
  }, [enforceSubscriptions])

  const exitRules = exitWorkflowFilter?.rules || []
  const exitRuleCount = exitRules.filter(r => r.type === 'filter').length
  const hasExitRules = exitRuleCount > 0

  function openHoursPopover(e) {
    setHoursAnchorEl(e.currentTarget)
  }

  function closeHoursPopover() {
    setHoursAnchorEl(null)
  }

  function saveRunningHours() {
    act.builder.updateRunningHours({
      openingTime: newOpeningTime,
      closingTime: newClosingTime
    }).then(closeHoursPopover)
  }

  function openSubscriptionsPopover(e) {
    setSubscriptionsAnchorEl(e.currentTarget)
  }

  function closeSubscriptionsPopover() {
    setSubscriptionsAnchorEl(null)
  }

  function saveEnforceSubscriptions() {
    act.builder.updateWorkflow({
      enforce_subscriptions: newEnforceSubscriptions
    }).then(closeSubscriptionsPopover)
  }

  function resume() {
    return act.local.openModal({ kind: 'resumeConfirm' })
  }

  function pause() {
    return act.builder.pause()
  }

  const hoursLabel = openingTime && closingTime ? `${openingTime} – ${closingTime}` : 'Not set'
  const subscriptionsLabel = enforceSubscriptions ? 'Only subscribed people' : 'Everyone'

  return (
    <div className='top-bar'>
      <div className='left-group'>
        <div className='title-block'>
          <BackLink href={backLink} />
          <h2>{name}</h2>
        </div>

        {/* Hours chip */}
        <button type='button' className='setting-pill' onClick={openHoursPopover}>
          <SvgIconsCalendar width={14} height={14} />
          <span>{hoursLabel}</span>
        </button>
        <Popover
          open={Boolean(hoursAnchorEl)}
          anchorEl={hoursAnchorEl}
          onClose={closeHoursPopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <div className='popover-content' style={{ minWidth: 260 }}>
            <p className='popover-title'>Running hours</p>
            <div className='popover-row'>
              <input
                type='time'
                className='input plain-focus'
                value={newOpeningTime}
                onChange={(e) => setNewOpeningTime(e.target.value)}
              />
              <span>to</span>
              <input
                type='time'
                className='input plain-focus'
                value={newClosingTime}
                onChange={(e) => setNewClosingTime(e.target.value)}
              />
            </div>
            <div className='popover-actions'>
              <Button primary small onClick={saveRunningHours}>Save</Button>
              <Button naked small onClick={closeHoursPopover}>Cancel</Button>
            </div>
          </div>
        </Popover>

        {/* Subscriptions chip */}
        <button type='button' className='setting-pill' onClick={openSubscriptionsPopover}>
          <SvgIconsMail width={14} height={14} />
          <span>
            {subscriptionsLabel}
            {organisationName && <span className='dark-grey'> ({organisationName})</span>}
          </span>
        </button>
        <Popover
          open={Boolean(subscriptionsAnchorEl)}
          anchorEl={subscriptionsAnchorEl}
          onClose={closeSubscriptionsPopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <div className='popover-content' style={{ minWidth: 280 }}>
            <p className='popover-title'>Subscriptions</p>
            <label>
              <input
                type='radio'
                name='enforce_subscriptions_popover'
                checked={newEnforceSubscriptions === true}
                onChange={() => setNewEnforceSubscriptions(true)}
              />
              Only subscribed people
            </label>
            <label>
              <input
                type='radio'
                name='enforce_subscriptions_popover'
                checked={newEnforceSubscriptions === false}
                onChange={() => setNewEnforceSubscriptions(false)}
              />
              Everyone (including unsubscribed)
            </label>
            <div className='popover-actions' style={{ marginTop: 4 }}>
              <Button primary small onClick={saveEnforceSubscriptions}>Save</Button>
              <Button naked small onClick={closeSubscriptionsPopover}>Cancel</Button>
            </div>
          </div>
        </Popover>

        {/* Exit Rules chip */}
        <button
          type='button'
          className={`setting-pill exit-rules-chip${hasExitRules ? '' : ' empty'}`}
          onClick={act.local.editExitRules}
          title='Exit Rules apply at every step — remove members who match these conditions, mid-workflow.'
        >
          <SvgIconsExitArrow width={14} height={14} />
          {hasExitRules ? (
            <span>Exit Rules · {exitRuleCount} condition{exitRuleCount !== 1 ? 's' : ''}</span>
          ) : (
            <span>Set Exit Rules</span>
          )}
        </button>
      </div>

      <div className='actions-group'>
        {state === 'draft' && <Button primary onClick={resume}>Launch</Button>}
        {state === 'paused' && <Button primary onClick={resume}>Resume</Button>}
        {state === 'running' && <ServerWaitButton secondary onClick={pause}>Pause</ServerWaitButton>}
      </div>
    </div>
  )
}
